import {
  ArrowsRightLeftIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  BeakerIcon,
  BellAlertIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  HeartIcon,
  LinkIcon,
  PuzzlePieceIcon,
  ServerStackIcon,
  QueueListIcon,
  Squares2X2Icon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";

/**
 * Static navigation for the Platform Owner console.
 *
 * Deliberately NOT derived from `featureRegistry`/`useSidebarState`: those resolve against tenant
 * permissions, tenant branding and the tenant feature registry, none of which a PLATFORM-scope
 * session has. Platform authority comes from the `platform_role` claim (see `PlatformOwnerRoute`).
 *
 * `available: false` marks a destination whose backend does not exist yet. Those still route — to a
 * `PlatformPlaceholderPage` naming the missing API — so the console's intended shape stays legible
 * without any screen pretending to hold real data.
 */
export interface PlatformNavItem {
  id: string;
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  available: boolean;
}

export interface PlatformNavSection {
  id: string;
  title: string;
  items: PlatformNavItem[];
}

export const PLATFORM_NAV: PlatformNavSection[] = [
  {
    id: "platform",
    title: "Platform",
    items: [
      {
        id: "overview",
        name: "Overview",
        href: "/platform",
        icon: Squares2X2Icon,
        available: true,
      },
      {
        id: "tenants",
        name: "Tenants",
        href: "/platform/tenants",
        icon: BuildingOffice2Icon,
        available: true,
      },
      {
        id: "users",
        name: "Users",
        href: "/platform/users",
        icon: UsersIcon,
        available: false,
      },
      {
        id: "plans",
        name: "Plans & Subscriptions",
        href: "/platform/plans",
        icon: CreditCardIcon,
        available: false,
      },
      {
        id: "payments",
        name: "Payments",
        href: "/platform/payments",
        icon: BanknotesIcon,
        available: false,
      },
      {
        id: "audit-logs",
        name: "Audit Logs",
        href: "/platform/audit-logs",
        icon: ClipboardDocumentListIcon,
        available: false,
      },
    ],
  },
  {
    id: "communications",
    title: "Communications",
    items: [
      {
        id: "whatsapp-operations",
        name: "WhatsApp Operations",
        href: "/platform/whatsapp",
        icon: ChatBubbleLeftRightIcon,
        available: true,
      },
      {
        id: "messaging-operations",
        name: "Messaging Queues",
        href: "/platform/messaging",
        icon: QueueListIcon,
        available: true,
      },
    ],
  },
  {
    id: "data",
    title: "Data Operations",
    items: [
      {
        id: "migrations",
        name: "Migrations",
        href: "/platform/migrations",
        icon: ServerStackIcon,
        available: true,
      },
      {
        id: "bulk-import",
        name: "Bulk Import",
        href: "/platform/bulk-import",
        icon: ArrowsRightLeftIcon,
        available: true,
      },
      {
        id: "legacy-migration",
        name: "Legacy Migration",
        href: "/platform/legacy-migration",
        icon: BeakerIcon,
        available: true,
      },
    ],
  },
  {
    id: "system",
    title: "System",
    items: [
      {
        id: "feature-flags",
        name: "Feature Flags",
        href: "/platform/feature-flags",
        icon: PuzzlePieceIcon,
        available: false,
      },
      {
        id: "settings",
        name: "System Settings",
        href: "/platform/settings",
        icon: Cog6ToothIcon,
        available: false,
      },
      {
        id: "integrations",
        name: "Integrations",
        href: "/platform/integrations",
        icon: LinkIcon,
        available: false,
      },
      {
        id: "notifications",
        name: "Notifications",
        href: "/platform/notifications",
        icon: BellAlertIcon,
        available: false,
      },
      {
        id: "health",
        name: "Platform Health",
        href: "/platform/health",
        icon: HeartIcon,
        available: false,
      },
    ],
  },
];
